import React, { FC, useEffect } from 'react'
import { Provider } from 'react-redux'
import { store } from '../../../store/store'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { 
  selectIsAuthenticated, 
  selectShouldShowSplashScreen, 
  selectCurrentToken,
  selectUser,
  selectIsFetchingUser
} from '../../../store/selectors'
import { fetchUserData, setSplashScreenLoading, forceLogout } from '../../../store/slices/authSlice'
import { LayoutSplashScreen } from '../../../_metronic/layout/core'
import { WithChildren } from '../../../_metronic/helpers'
import Cookies from 'js-cookie'

const AuthInit: FC<WithChildren> = ({ children }) => {
  const dispatch = useAppDispatch()
  const isAuthenticated = useAppSelector(selectIsAuthenticated)
  const shouldShowSplashScreen = useAppSelector(selectShouldShowSplashScreen)
  const currentToken = useAppSelector(selectCurrentToken)
  const user = useAppSelector(selectUser)
  const isFetchingUser = useAppSelector(selectIsFetchingUser)
  
  // Initialize subscription status monitoring
  // useSubscriptionStatusMonitor()

  // Use ref to prevent multiple initialization calls
  const initializationRef = React.useRef(false)
  
  useEffect(() => {
    // Prevent multiple initialization calls
    if (initializationRef.current) return
    initializationRef.current = true
    
    const initializeAuth = async () => {
      try {
        
        // Handle cookie-to-localStorage migration
        const cookieValue = Cookies.get('auth_token')
        if (cookieValue) {
          try {
            const authObj = { token: cookieValue }
            const objectString = JSON.stringify(authObj)
            localStorage.setItem('kt-auth-react-v', objectString)
            Cookies.remove('auth_token')
            
            // The auth state will be updated by the store initialization
            // No need to reload the page, just fetch user data
            if (isAuthenticated && currentToken) {
              await dispatch(fetchUserData())
            }
            return
          } catch (error) {
            Cookies.remove('auth_token')
          }
        }
        
        if (isAuthenticated && currentToken) {
          // Fetch user data if we have a token but no user data
          if (!user || !user.id) {
            await dispatch(fetchUserData())
          } else {
            // If we already have user data, just hide the splash screen
            dispatch(setSplashScreenLoading(false))
          }
        } else {
          // Show splash screen briefly before redirecting to login
          setTimeout(() => {
            dispatch(setSplashScreenLoading(false))
            // Don't navigate here - let AppRoutes handle it
          }, 500)
        }
      } catch (error) {
        dispatch(forceLogout())
        setTimeout(() => {
          // Don't navigate here - let AppRoutes handle it
        }, 500)
      }
    }
    
    initializeAuth()
  },[dispatch, isAuthenticated, currentToken, user]) // Empty dependency array to run only once
  
  // Hide splash screen once user data is loaded
  useEffect(() => {
    if (isAuthenticated && user && !isFetchingUser) {
      dispatch(setSplashScreenLoading(false))
      // Don't navigate here - let AppRoutes handle the navigation
    }
  }, [dispatch, isAuthenticated, user, isFetchingUser])
  
  // Show splash screen during authentication check
  if (shouldShowSplashScreen) {
    return <LayoutSplashScreen />
  }
  
  return <>{children}</>
}

const ReduxAuthProvider: FC<WithChildren> = ({ children }) => {
  return (
    <Provider store={store}>
      <AuthInit>
        {children}
      </AuthInit>
    </Provider>
  )
}

export default ReduxAuthProvider
